/**
 * Centralized formatting utilities for consistent display across the app.
 */

import { GRADUATION_THRESHOLD_SOL } from '@/lib/meteora/graduation';

/**
 * Format market cap / FDV for display
 */
export function formatMarketCap(value: number | null): string {
  if (value === null || value === undefined) return '--';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

/**
 * Format a number with K/M suffixes
 */
export function formatNumber(num: number | null | undefined): string {
  if (num == null || isNaN(num)) return '--';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

/**
 * Compact count for UI badges next to tab labels: "312", "1.2k", "12.4k", "1.2m".
 * Lowercase suffix, drops trailing ".0".
 */
export function formatCompactCount(num: number | null | undefined): string {
  if (num == null || isNaN(num) || num < 0) return '0';
  if (num < 1_000) return Math.floor(num).toString();
  const stripZero = (s: string) => s.replace(/\.0$/, '');
  if (num < 1_000_000) return `${stripZero((num / 1_000).toFixed(1))}k`;
  return `${stripZero((num / 1_000_000).toFixed(1))}m`;
}

/**
 * Format currency with $ prefix
 */
export function formatCurrency(num: number | null | undefined): string {
  if (num == null || isNaN(num)) return '--';
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(2)}`;
}

/**
 * Format SOL amount with appropriate precision and K/M suffixes
 */
export function formatSolAmount(sol: number | null | undefined): string {
  if (sol == null || isNaN(sol)) return '-- SOL';
  if (sol >= 1_000_000) return `${(sol / 1_000_000).toFixed(2)}M SOL`;
  if (sol >= 1_000) return `${(sol / 1_000).toFixed(2)}K SOL`;
  if (sol >= 1) return `${sol.toFixed(2)} SOL`;
  if (sol >= 0.001) return `${sol.toFixed(3)} SOL`;
  return `${sol.toFixed(4)} SOL`;
}

/**
 * Format percentage change with sign and color
 */
export function formatPercentChange(value: number | null): {
  text: string;
  isPositive: boolean;
  color: string;
} {
  if (value === null || value === undefined || value === 0) {
    return { text: '--', isPositive: true, color: 'text-muted-foreground' };
  }

  const isPositive = value >= 0;
  const prefix = isPositive ? '+' : '';
  const color = isPositive ? 'text-[#4ADE80]' : 'text-[#F87171]';

  let text: string;
  if (Math.abs(value) > 999.99) {
    text = value >= 0 ? '>999%' : '<-999%';
  } else {
    text = `${prefix}${value.toFixed(2)}%`;
  }

  return { text, isPositive, color };
}

/**
 * Format time ago in compact form ("1m ago", "2h ago", "3d ago")
 */
export function formatTimeAgo(date: Date | string): string {
  const now = new Date();
  const then = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - then.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return '< 1m';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  return `${Math.floor(diffDays / 30)}mo`;
}

/**
 * Format time ago with "ago" suffix
 */
export function formatTimeAgoWithSuffix(date: Date | string): string {
  const compact = formatTimeAgo(date);
  if (compact === '< 1m') return 'just now';
  return `${compact} ago`;
}

/**
 * Format token amount with commas and appropriate decimal places.
 * Shows up to 2 decimals, omitting trailing zeros.
 */
export function formatTokenAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '0';

  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Format ownership percentage with adaptive precision for small holdings.
 * - ≥0.01% → 2 decimals (e.g., "5.00%")
 * - ≥0.001% → 3 decimals (e.g., "0.067%")
 * - ≥0.0001% → 4 decimals (e.g., "0.0067%")
 * - >0 but <0.0001% → "<0.0001%"
 * - 0 → "0%"
 */
export function formatOwnershipPercent(
  tokens: number,
  totalSupply: number = 1_000_000_000
): string {
  if (tokens <= 0) return '0%';

  const pct = (tokens / totalSupply) * 100;

  if (pct >= 0.01) return `${pct.toFixed(2)}%`;
  if (pct >= 0.001) return `${pct.toFixed(3)}%`;
  if (pct >= 0.0001) return `${pct.toFixed(4)}%`;
  return '<0.0001%';
}

/**
 * Creator fee rates by pool phase (effective rates of trade volume).
 * Re-exported from src/lib/constants/feeShares.ts (the canonical mirror of
 * supabase/functions/_shared/feeShares.ts; CI verifies all 3 are byte-equal).
 *
 * - Bonding curve (DBC v5): 2% × 80% LP × 50% creator = 0.80%
 * - Post-graduation (DAMM v2): 2% × 80% LP × 50% creator = 0.80%
 *
 * Both numerators reflect Meteora's 20% off-the-top cut. Per cp-amm
 * split_fees, referral_fee_percent has zero current effect (Jupiter does
 * not pass a referrer); see issues-queue O15.
 */
export {
  CREATOR_RATE_BONDING_DEFAULT as CREATOR_FEE_RATE_BONDING,
  CREATOR_RATE_GRADUATED_DEFAULT as CREATOR_FEE_RATE_GRADUATED,
} from '@/lib/constants/feeShares';

/**
 * NOTE: calculateCreatorFeesUsd and calculateCreatorFeesFromUsdVolume were
 * removed. They multiplied a SOL volume by the live SOL price to derive a
 * "creator fees USD" value, which caused historical earnings to fluctuate
 * with SOL price (the bug fixed in this PR). Read the locked snapshot from
 * pool_stats via getLockedCreatorFeesUsd in src/lib/solUsdConversion.ts.
 */

/**
 * Format bonding curve progress
 */
export function formatBondingProgress(
  currentSol: number,
  graduationSol: number = GRADUATION_THRESHOLD_SOL
): {
  progress: number;
  text: string;
  isGraduated: boolean;
} {
  const progress = Math.min(100, (currentSol / graduationSol) * 100);
  const isGraduated = progress >= 100;
  const text = isGraduated
    ? 'Graduated!'
    : `${currentSol.toFixed(1)} / ${graduationSol} SOL`;

  return { progress, text, isGraduated };
}
